import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  FormInputText,
  InputLabel,
  PaginatedSearchDropdown,
  PhoneNumberInput,
  Text,
} from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { PageHeading } from '@rideiq/shared/components/PageHeading';

import { useFlags } from '../../../../src/contexts/LDContext';
import { JobTitleFormField } from '../../../organizers/components/UserForm/JobTitleFormField';
import { useSignupLocationOptions } from '../../hooks/useSignupLocationOptions';

import { Container, StyledAction } from './styles';
import { onsiteStaffSchema, schema, ssoOnsiteStaffSchema, ssoSchema } from './validationSchema';

export interface NewUserFormProps {
  user: UserFormData | OnsiteStaffFormData | OnsiteStaffUser;
  handleNewUser: (data, e) => void;
  confirmationToken: string | null;
  disableSubmitButton: boolean;
  ssoRequired?: boolean;
}

type formData = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  mobilePhoneNumber: string;
  password: string;
  passwordConfirmation: string;
  location: AccountLocation | null;
};

export const NewUserForm: FC<NewUserFormProps> = ({
  user,
  handleNewUser,
  confirmationToken,
  disableSubmitButton,
  ssoRequired = false,
}) => {
  const isOnsiteStaffUser = user?.roles.includes('onsite_staff');
  const { enableDailyQueueAssignedLocations, ssoEnabled, jobTitleDropdownEnabled } = useFlags();
  const defaultValues = isOnsiteStaffUser
    ? {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        password: '',
        passwordConfirmation: '',
        roles: user.roles || [],
        jobTitle: (user as OnsiteStaffUser).jobTitle || '',
        location: null,
      }
    : { ...user, mobilePhoneNumber: parsePhoneNumber((user as UserFormData).mobilePhoneNumber) };
  const validationSchema = getValidationSchema({
    isOnsiteStaffUser,
    ssoRequired,
    enableDailyQueueAssignedLocations,
  });
  const methods = useForm<formData>({
    defaultValues: useMemo(() => {
      return defaultValues;
    }, [user]),
    resolver: yupResolver(validationSchema),
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = methods;
  const jobTitleOptions = (user as UserFormData)?.jobTitleOptions || [];
  const showJobTitleTextField = isOnsiteStaffUser && !jobTitleDropdownEnabled;

  return (
    <Container isOnsiteStaff={isOnsiteStaffUser}>
      <PageHeading variant="h1">Sign Up</PageHeading>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleNewUser)}>
          <Controller
            name="firstName"
            control={control}
            render={({ field: { value, onChange, name } }) =>
              enableDailyQueueAssignedLocations ? (
                <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                  <Box minWidth="150px" pt={1}>
                    <InputLabel fieldtype="required" htmlFor="firstName">
                      First Name
                    </InputLabel>
                  </Box>
                  <Box flex={1}>
                    <FormInputText
                      id="firstName"
                      value={value}
                      inputProps={{
                        'data-testid': 'firstName',
                      }}
                      placeholder="First Name (Required)"
                      name={name}
                      type="text"
                      variant="outlined"
                      error={!!errors.firstName}
                      autoFocus
                      fullWidth
                      helperText={errors.firstName ? errors.firstName.message : ''}
                      onChange={onChange}
                    />
                  </Box>
                </Box>
              ) : (
                <FormInputText
                  id="firstName"
                  value={value}
                  inputProps={{
                    'data-testid': 'firstName',
                  }}
                  placeholder="First Name (Required)"
                  name={name}
                  type="text"
                  variant="outlined"
                  error={!!errors.firstName}
                  autoFocus
                  fullWidth
                  helperText={errors.firstName ? errors.firstName.message : ''}
                  onChange={onChange}
                />
              )
            }
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field: { value, onChange, name } }) =>
              enableDailyQueueAssignedLocations ? (
                <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                  <Box minWidth="150px" pt={1}>
                    <InputLabel fieldtype="required" htmlFor="lastName">
                      Last Name
                    </InputLabel>
                  </Box>
                  <Box flex={1}>
                    <FormInputText
                      id="lastName"
                      value={value}
                      inputProps={{
                        'data-testid': 'lastName',
                      }}
                      placeholder="Last Name (Required)"
                      name={name}
                      type="text"
                      variant="outlined"
                      fullWidth
                      error={!!errors.lastName}
                      helperText={errors.lastName ? errors.lastName.message : ''}
                      onChange={onChange}
                    />
                  </Box>
                </Box>
              ) : (
                <FormInputText
                  id="lastName"
                  value={value}
                  inputProps={{
                    'data-testid': 'lastName',
                  }}
                  placeholder="Last Name (Required)"
                  name={name}
                  type="text"
                  variant="outlined"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName ? errors.lastName.message : ''}
                  onChange={onChange}
                />
              )
            }
          />

          {showJobTitleTextField && (
            <Controller
              name="jobTitle"
              control={control}
              render={({ field: { value, onChange, name } }) =>
                enableDailyQueueAssignedLocations ? (
                  <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                    <Box minWidth="150px" pt={1}>
                      <InputLabel fieldtype="required" htmlFor="jobTitle">
                        Job Title
                      </InputLabel>
                    </Box>
                    <Box flex={1}>
                      <FormInputText
                        id="jobTitle"
                        value={value}
                        inputProps={{
                          'data-testid': 'jobTitle',
                        }}
                        placeholder="Job Title (Required)"
                        name={name}
                        type="text"
                        variant="outlined"
                        fullWidth
                        error={!!errors.jobTitle}
                        helperText={errors.jobTitle ? errors.jobTitle.message : ''}
                        onChange={onChange}
                      />
                    </Box>
                  </Box>
                ) : (
                  <FormInputText
                    id="jobTitle"
                    value={value}
                    inputProps={{
                      'data-testid': 'jobTitle',
                    }}
                    placeholder="Job Title (Required)"
                    name={name}
                    type="text"
                    variant="outlined"
                    fullWidth
                    error={!!errors.jobTitle}
                    helperText={errors.jobTitle ? errors.jobTitle.message : ''}
                    onChange={onChange}
                  />
                )
              }
            />
          )}
          {!isOnsiteStaffUser && (
            <Controller
              control={control}
              name={'mobilePhoneNumber'}
              render={({ field: { onChange, value, name } }) =>
                enableDailyQueueAssignedLocations ? (
                  <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                    <Box minWidth="150px" pt={1}>
                      <InputLabel htmlFor="mobilePhoneNumber">Cell Phone</InputLabel>
                    </Box>
                    <Box flex={1}>
                      <PhoneNumberInput
                        variant="outlined"
                        name={name}
                        fullWidth
                        placeholder="Cell phone"
                        onValueChange={(value) => onChange(value)}
                        initialValue={parsePhoneNumber(value)}
                        error={!!errors.mobilePhoneNumber}
                        helperText={errors.mobilePhoneNumber ? errors.mobilePhoneNumber.message : ''}
                      />
                    </Box>
                  </Box>
                ) : (
                  <PhoneNumberInput
                    variant="outlined"
                    name={name}
                    fullWidth
                    placeholder="Cell phone"
                    onValueChange={(value) => onChange(value)}
                    initialValue={parsePhoneNumber(value)}
                    error={!!errors.mobilePhoneNumber}
                    helperText={errors.mobilePhoneNumber ? errors.mobilePhoneNumber.message : ''}
                  />
                )
              }
            />
          )}
          {jobTitleDropdownEnabled && (
            <Box display="flex" alignItems="flex-start" gap={2} justifyContent="space-between">
              <JobTitleFormField jobTitleOptions={jobTitleOptions} isFetching={false} isRequired={isOnsiteStaffUser} />
            </Box>
          )}
          {!(ssoEnabled && ssoRequired) && (
            <>
              <Controller
                name="password"
                control={control}
                render={({ field: { value, onChange } }) =>
                  enableDailyQueueAssignedLocations ? (
                    <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                      <Box minWidth="150px" pt={1}>
                        <InputLabel fieldtype="required" htmlFor="password">
                          Password
                        </InputLabel>
                      </Box>
                      <Box flex={1}>
                        <FormInputText
                          id="password"
                          value={value}
                          inputProps={{
                            'data-testid': 'password',
                          }}
                          placeholder="New password (Required)"
                          name="password"
                          type="password"
                          variant="outlined"
                          error={!!errors.password}
                          fullWidth
                          helperText={errors.password ? errors.password.message : ''}
                          onChange={onChange}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <FormInputText
                      id="password"
                      value={value}
                      inputProps={{
                        'data-testid': 'password',
                      }}
                      placeholder="New password (Required)"
                      name="password"
                      type="password"
                      variant="outlined"
                      error={!!errors.password}
                      fullWidth
                      helperText={errors.password ? errors.password.message : ''}
                      onChange={onChange}
                    />
                  )
                }
              />
              <Controller
                name="passwordConfirmation"
                control={control}
                render={({ field: { value, onChange } }) =>
                  enableDailyQueueAssignedLocations ? (
                    <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                      <Box minWidth="150px" pt={1}>
                        <InputLabel fieldtype="required" htmlFor="passwordConfirmation">
                          Confirm Password
                        </InputLabel>
                      </Box>
                      <Box flex={1}>
                        <FormInputText
                          id="passwordConfirmation"
                          value={value}
                          inputProps={{
                            'data-testid': 'passwordConfirmation',
                          }}
                          placeholder="Confirm new password (Required)"
                          name="passwordConfirmation"
                          type="password"
                          variant="outlined"
                          error={!!errors.passwordConfirmation}
                          fullWidth
                          helperText={errors.passwordConfirmation ? errors.passwordConfirmation.message : ''}
                          onChange={onChange}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <FormInputText
                      id="passwordConfirmation"
                      value={value}
                      inputProps={{
                        'data-testid': 'passwordConfirmation',
                      }}
                      placeholder="Confirm new password (Required)"
                      name="passwordConfirmation"
                      type="password"
                      variant="outlined"
                      error={!!errors.passwordConfirmation}
                      fullWidth
                      helperText={errors.passwordConfirmation ? errors.passwordConfirmation.message : ''}
                      onChange={onChange}
                    />
                  )
                }
              />
            </>
          )}
          {isOnsiteStaffUser &&
            (enableDailyQueueAssignedLocations ? (
              <>
                <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                  <Box minWidth="150px" pt={1}>
                    <InputLabel htmlFor="role">Role</InputLabel>
                  </Box>
                  <Box flex={1}>
                    <Autocomplete
                      value={'Onsite Staff - View Only'}
                      id="role"
                      data-testid="role"
                      options={[{ value: 'Onsite Staff - View Only', label: 'Onsite Staff - View Only' }]}
                      isOptionEqualToValue={(value) => 'Onsite Staff - View Only' === value.value}
                      disabled
                      renderInput={(params) => <FormInputText {...params} disabled variant="outlined" />}
                      onChange={() => null}
                    />
                  </Box>
                </Box>

                <Controller
                  control={control}
                  name="location"
                  render={() => (
                    <Box display="flex" alignItems="flex-start" gap={2} mb={16}>
                      <Box minWidth="150px" pt={1}>
                        <InputLabel htmlFor="location">Location</InputLabel>
                      </Box>
                      <Box flex={1}>
                        <LocationDisplay user={user} />
                      </Box>
                    </Box>
                  )}
                />
              </>
            ) : (
              <>
                <Autocomplete
                  value={'Onsite Staff - View Only'}
                  id="role"
                  data-testid="role"
                  options={[{ value: 'Onsite Staff - View Only', label: 'Onsite Staff - View Only' }]}
                  isOptionEqualToValue={(value) => 'Onsite Staff - View Only' === value.value}
                  disabled
                  renderInput={(params) => <FormInputText {...params} disabled variant="outlined" />}
                  onChange={() => null}
                />

                <Controller
                  control={control}
                  name="location"
                  render={({ field: { onChange, value, name } }) => (
                    <>
                      <SelectLocationForOnsiteStaff
                        defaultLocation={(user as OnsiteStaffUser).defaultAccountLocation}
                        value={value || (user as OnsiteStaffUser).defaultAccountLocation}
                        formSetValue={setValue}
                        onChange={onChange}
                        name={name}
                        id={name}
                        error={!!errors.location}
                        helperText={errors.location?.message}
                        confirmationToken={confirmationToken}
                      />
                      <Text ml={3} mb={6} textColor="secondaryText" fontWeight={400} fontSize="12px">
                        You’ll be assigned but not limited to this location
                      </Text>
                    </>
                  )}
                />
              </>
            ))}
          <StyledAction>
            <Button type="submit" color="primary" data-testid="submitButton" disabled={disableSubmitButton}>
              {isOnsiteStaffUser ? 'Continue' : 'Get Started'}
            </Button>
          </StyledAction>
        </form>
      </FormProvider>
    </Container>
  );
};

const parsePhoneNumber = (phoneNum) => phoneNum.replace(/(\+1)/, '');

const getValidationSchema = ({
  isOnsiteStaffUser,
  ssoRequired,
  enableDailyQueueAssignedLocations,
}: {
  isOnsiteStaffUser: boolean;
  ssoRequired: boolean;
  enableDailyQueueAssignedLocations: boolean;
}) => {
  if (isOnsiteStaffUser) {
    return ssoRequired
      ? ssoOnsiteStaffSchema(enableDailyQueueAssignedLocations)
      : onsiteStaffSchema(enableDailyQueueAssignedLocations);
  }

  return ssoRequired ? ssoSchema : schema;
};

const LocationDisplay = ({ user }) => {
  const accountLocationNames = user?.accountLocationNames || [];

  if (accountLocationNames.length === 0) {
    return <Text data-testid="no-locations">No locations available</Text>;
  }

  const locationText = accountLocationNames.join(', ');

  return <Text variant="body2">{locationText}</Text>;
};

const SelectLocationForOnsiteStaff = ({
  onChange,
  error,
  helperText,
  confirmationToken,
  defaultLocation,
  name,
  value,
  formSetValue,
  ...props
}) => {
  const { debounceHandleQuery, signupLocationOptions, handlePagination, queryParams, isLoading } =
    useSignupLocationOptions(confirmationToken);
  const [locationInputValue, setLocationInputValue] = useState<string>('');
  const [currentValue, setCurrentValue] = useState<Pick<AccountLocation, 'id' | 'name' | 'address'>>(value);

  useEffect(() => {
    setLocationInputValue(
      defaultLocation.name ? `${defaultLocation.name}, ${defaultLocation.address}` : defaultLocation.address
    );
    setCurrentValue(value);
    formSetValue(name, currentValue, { shouldValidate: true });
  }, [defaultLocation]);

  return (
    <PaginatedSearchDropdown
      {...props}
      hidden={false}
      options={signupLocationOptions.map((loc) => ({
        label: loc.name ? `${loc.name}, ${loc.address}` : loc.address,
        ...loc,
      }))}
      perPage={queryParams.perPage}
      pageNumber={queryParams.page}
      isLoading={isLoading}
      loadNextPage={handlePagination}
      inputValue={locationInputValue}
      setInputValue={setLocationInputValue}
      onChange={(_, value) => {
        debounceHandleQuery('');
        onChange(value);
        if (!value || typeof value === 'string') {
          return;
        }
        setCurrentValue(value);
        setLocationInputValue(value.label);
      }}
      boldCharacterMatch={true}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <FormInputText
          {...params}
          variant="outlined"
          fullWidth
          id={name}
          name={name}
          placeholder="Location (Required)"
          onChange={(e) => {
            debounceHandleQuery(e.target.value);
          }}
          data-testid="location"
          error={error}
          helperText={helperText}
          sx={{ marginBottom: '0 !important' }}
        />
      )}
    />
  );
};
